import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { createBulkJob } from '@/api/domains'
import { AWS_ACCOUNTS } from '@/lib/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Loader2 } from 'lucide-react'

const bulkVerifySchema = z.object({
  account_id: z.string().min(1, 'Please select an account'),
  domains: z
    .string()
    .min(1, 'At least one domain is required')
    .refine(
      (val) => {
        const domains = val.split('\n').filter((d) => d.trim())
        return domains.length > 0 && domains.length <= 100
      },
      'Enter between 1 and 100 domains'
    ),
  configuration_set_name: z.string().optional(),
  mail_from_subdomain: z.string().optional(),
})

type BulkVerifyFormData = z.infer<typeof bulkVerifySchema>

export function BulkVerifyForm() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm<BulkVerifyFormData>({
    resolver: zodResolver(bulkVerifySchema),
  })

  const domainsInput = watch('domains') || ''
  const domainCount = domainsInput.split('\n').filter((d) => d.trim()).length

  const onSubmit = async (data: BulkVerifyFormData) => {
    setIsSubmitting(true)
    setError(null)

    const domains = data.domains
      .split('\n')
      .map((d) => d.trim().toLowerCase())
      .filter((d) => d.length > 0)

    const response = await createBulkJob({
      account_id: data.account_id,
      domains,
      configuration_set_name: data.configuration_set_name || undefined,
      mail_from_subdomain: data.mail_from_subdomain || undefined,
    })

    if (!response.success || !response.data) {
      setError(response.error || 'Failed to create bulk job')
      setIsSubmitting(false)
      return
    }

    navigate(`/dashboard/domains/verify/jobs/${response.data.job_id}`)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/dashboard/domains">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold">Bulk Domain Verification</h1>
          <p className="text-muted-foreground">Verify multiple domains at once (up to 100)</p>
        </div>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle>Bulk Verification</CardTitle>
          <CardDescription>
            Enter domains one per line. All domains will be verified in the same AWS account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">{error}</div>
            )}

            <div className="space-y-2">
              <Label htmlFor="account_id">AWS Account</Label>
              <Controller
                name="account_id"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange} disabled={isSubmitting}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select account...">
                        {field.value && (
                          <span>
                            {AWS_ACCOUNTS.find((a) => a.id === field.value)?.alias}
                            <span className="ml-2 text-muted-foreground font-mono text-xs">{field.value}</span>
                          </span>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {AWS_ACCOUNTS.map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{account.alias}</span>
                            <span className="text-xs text-muted-foreground font-mono">{account.id}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.account_id && (
                <p className="text-sm text-destructive">{errors.account_id.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="configuration_set_name">
                Configuration Set <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="configuration_set_name"
                type="text"
                placeholder="my-config-set"
                {...register('configuration_set_name')}
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mail_from_subdomain">
                Mail From Subdomain{' '}
                <span className="text-muted-foreground">(optional, default: mail)</span>
              </Label>
              <Input
                id="mail_from_subdomain"
                type="text"
                placeholder="mail"
                {...register('mail_from_subdomain')}
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="domains">Domains (one per line)</Label>
                <Badge variant="secondary">{domainCount} / 100</Badge>
              </div>
              <Textarea
                id="domains"
                placeholder={'example.com\nsubdomain.example.com\nanother-domain.com'}
                rows={10}
                className="font-mono text-sm"
                {...register('domains')}
                disabled={isSubmitting}
              />
              {errors.domains && (
                <p className="text-sm text-destructive">{errors.domains.message}</p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting || domainCount === 0}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                `Verify ${domainCount} Domain${domainCount !== 1 ? 's' : ''}`
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
